import {
  IconButton,
  Button,
  ButtonLink,
  FilterDropdown,
  Spinner,
  Modal,
  PersonAvatar,
  InputSelect,
  InputText,
  Switch,
  Checkbox,
} from '@teo/components';
import {
  useState,
  useContext,
  useEffect,
  forwardRef,
  Fragment,
  ChangeEvent,
  InputHTMLAttributes,
  Ref,
} from 'react';
import { Link } from 'react-router-dom';
import {
  OpenExternalLink,
  Search,
  IconComponent,
  ChevronDownFilled,
  ArrowRight,
} from '@teo/components/icons';
import { langData } from '@/mocks/landData';
import { useTranslation } from 'react-i18next';
import { useGet } from '@/query/fetchHooks';
import { Membercoursecontentdetails } from '@/models/Database';
import { AuthContext } from '@/components/Contexts/AuthContext';
import { axios } from '@/axios';
import { config } from '@/config';
import { ConfirmTranslationModal } from '@/components/ConfirmTranslationModal/ConfirmTranslationModal';
import { Combobox } from '@headlessui/react';
import clsx from 'clsx';

const translateServices = [
  {
    label: 'Google',
    value: 'google',
  },
  {
    label: 'ChatGPT',
    value: 'gpt',
  },
  /*
  , {
    label: 'Deepl',
    value: 'deepl'
  }, {
    label: 'fake/test',
    value: 'fake'
  }
  */
];

type InputElementType = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'placeholder'
>;

export type InputTextProps = {
  Icon?: IconComponent;
  customStyles?: string;
  hasError?: boolean;
  iconPosition?: 'left' | 'right';
  innerRef?: Ref<HTMLInputElement>;
  inputClassName?: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  prefix?: string;
  suffix?: string;
  textAlign?: 'left' | 'center';
} & InputElementType;

const defaultFilterFn =
  (query: string) =>
  ({ name }: any) =>
    name.toLowerCase().includes(query.toLowerCase());

// eslint-disable-next-line react/display-name
const CustomInput = forwardRef<HTMLInputElement, InputTextProps>(
  (props, ref: any) => <InputText Icon={Search} {...props} innerRef={ref} />
);

export const SelectDropdown = ({
  dropdownProps,
  options,
  onChange,
  value,
  buttonText,
}: any) => {
  const [query, setQuery] = useState<any>('');
  const filteredOptions =
    query === '' ? options : options.filter(defaultFilterFn(query));

  return (
    <Combobox value={value} onChange={onChange}>
      {({ open }) => (
        <>
          <Combobox.Button
            className={clsx(
              'flex h-[40px] w-full items-center gap-2 rounded-md bg-grey-01 py-2 px-3 text-base text-grey-08 hover:bg-grey-02',
              'outline-primary-02 focus:outline focus:outline-4'
            )}
          >
            {value && (
              <img
                src={`/images/flags/${value.value}.svg`}
                onError={(e) => {
                  e.currentTarget.src = '/images/icons/lp_end.svg';
                }}
                alt={`${value.name} flag`}
                className="
                                h-[20px] w-[20px]
                                rounded-[50%]
                                object-cover
                                "
              />
            )}
            {value ? `${value.name}` : buttonText}
            <>
              {open ? (
                <ChevronDownFilled className="ml-auto h-5 w-5 " />
              ) : (
                <ChevronDownFilled className="ml-auto h-5 w-5 rotate-180" />
              )}
            </>
          </Combobox.Button>
          {open && (
            <div className="relative">
              <div className={`absolute `}>
                <div className="mt-2 w-52 rounded-lg bg-white p-2 drop-shadow-s md:w-60 lg:w-72">
                  <Combobox.Input
                    onChange={(event) => setQuery(event.currentTarget.value)}
                    value={query}
                    as={CustomInput}
                  />
                  <Combobox.Options
                    className={clsx([
                      'mt-1 max-h-40 space-y-1 overflow-auto pr-1',
                    ])}
                    {...dropdownProps}
                  >
                    {filteredOptions.map((option: any) => (
                      <Combobox.Option
                        as={Fragment}
                        key={option.id}
                        value={option}
                      >
                        {({ selected }) => (
                          <div
                            className={clsx([
                              'flex items-center gap-2 rounded px-3 py-2 text-sm',
                              'hover:cursor-pointer hover:bg-grey-01 active:bg-grey-02',
                              selected ? 'bg-secondary-01' : 'bg-transparent',
                            ])}
                          >
                            <img
                              src={`/images/flags/${option.value}.svg`}
                              onError={(e) => {
                                e.currentTarget.src =
                                  '/images/icons/lp_end.svg';
                              }}
                              alt={`${option.name} flag`}
                              className="
                                h-[20px] w-[20px]
                                rounded-[50%]
                                object-cover
                                "
                            />
                            {option.name}
                          </div>
                        )}
                      </Combobox.Option>
                    ))}
                  </Combobox.Options>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </Combobox>
  );
};

export const TranslatePublication = (props: any) => {
  const { auth } = useContext(AuthContext);
  const [selectedService, setSelectedService] = useState('google');
  const [asyncStarted, setAsyncStarted] = useState(false); // default to ChatGPT or your preferred default
  const [syncingTranslationId, setSyncingTranslationId] = useState<
    number | null
  >(null);
  const [competenceSyncMessage, setCompetenceSyncMessage] = useState<{
    text: string;
    isError: boolean;
  } | null>(null);

  const [userTranslations, setUserTranslations] = useState<any>([]);

  const languageMasterId =
    props.publication.languageMasterId || props.publication.id;

  const translationsResult = useGet<Array<Membercoursecontentdetails>>(
    `/membercoursecontentdetails`,
    {
      languageMasterId: `${languageMasterId},OR,coursecontentId=${props.publication.id},OR,coursecontentId=${languageMasterId}`,
      organisationId: auth?.user?.activeOrganisationId,
      sort: 'createdAt',
    }
  );

  const isGeneralAdmin = (auth?.user?.role || 0) >= 90;

  // General admins always have access to ChatGPT translate; other members only
  // when their organisation has explicitly enabled it (Organisation >
  // Licensing and billing > Options).
  const orgChatGptTranslateEnabled =
    isGeneralAdmin || !!auth?.user?.useTeoAiChatGptTranslate;

  const translationLanguages = langData.filter(
    (language) =>
      language.value !== 'en' &&
      (!['en-GB', 'en-US'].includes(language.value) ||
        orgChatGptTranslateEnabled)
  );

  const availableTranslateServices = translateServices.filter(
    (service) => service.value !== 'gpt' || orgChatGptTranslateEnabled
  );

  const translations: Array<Membercoursecontentdetails> | [] =
    translationsResult?.data?.data || [];

  const { t } = useTranslation();
  const [lang, setLang] = useState({
    id: '',
    name: '',
    value: '',
  });

  const [selectLang, setSelectLang] = useState({
    id: '',
    name: '',
    value: '',
  });
  const [isOpenConfirmModal, setIsOpenConfirmModal] = useState(false);

  const basePath = window.location.pathname.slice(
    0,
    window.location.pathname.lastIndexOf('/')
  );
  const [translateSubtitles, setTranslateSubtitles] = useState(true);

  useEffect(() => {
    if (translations?.length > 1) {
      translations?.map(async (translation: any) => {
        if (translation) {
          const test = translation?.id;
          await axios
            .get(
              `${config.backend}/contentcreators?coursecontentId=${
                test && translation?.id
              }&userId=${auth?.user?.id}`
            )
            .then((result: any) => {
              const newCreatorList = result?.data;
              const creatorId =
                newCreatorList.map((item: any) => item?.coursecontentId) || [];
              setUserTranslations((prev: any) => [...prev, ...creatorId]);
            });
        }
      });
    }
  }, [translations]);

  const handleTranslate = async (newLang: any) => {
    setIsOpenConfirmModal(false);
    const response = await axios.post(
      `${config.backend}/api/translate/${languageMasterId}?lang=${newLang?.value}&service=${selectedService}&translateSubtitles=${translateSubtitles}`,
      {
        organisationId: auth?.user?.activeOrganisationId,
        translateSubtitles: translateSubtitles,
      }
    );

    if (response.status === 202) {
      setAsyncStarted(true);
      return;
    }

    translationsResult.refetch().then(() => {
      setLang({ id: '', name: '', value: '' });
      setSelectLang({ id: '', name: '', value: '' });
    });
  };

  const handleCompetenceSync = async (translation: any) => {
    setSyncingTranslationId(translation.id);
    setCompetenceSyncMessage(null);

    try {
      const previewResponse = await axios.post(
        `${config.backend}/api/translate/${translation.id}/sync-competences`,
        { dryRun: true }
      );
      const levels = Object.values(
        previewResponse.data?.levels || {}
      ) as Array<{
        added: number;
        updated: number;
        removed: number;
      }>;
      const totals = levels.reduce(
        (result, level) => ({
          added: result.added + level.added,
          updated: result.updated + level.updated,
          removed: result.removed + level.removed,
        }),
        { added: 0, updated: 0, removed: 0 }
      );

      if (totals.added + totals.updated + totals.removed === 0) {
        setCompetenceSyncMessage({
          text: t('pages.create_edit_course.competence_sync_no_changes'),
          isError: false,
        });
        return;
      }

      await axios.post(
        `${config.backend}/api/translate/${translation.id}/sync-competences`,
        { dryRun: false }
      );
      setCompetenceSyncMessage({
        text: t('pages.create_edit_course.competence_sync_complete'),
        isError: false,
      });
    } catch (error) {
      setCompetenceSyncMessage({
        text: t('pages.create_edit_course.competence_sync_failed'),
        isError: true,
      });
    } finally {
      setSyncingTranslationId(null);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();

        props.onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [props.onClose]);

  return (
    <>
      <Modal isOpen={true}>
        <Modal.Header
          onClose={() => {
            props.onClose();
          }}
        >
          {t('pages.create_edit_course.language')}
        </Modal.Header>
        <Modal.Body>
          <div className=" -ml-6 -mr-6">
            <div className="relative  -mt-6 max-h-[270px] overflow-y-auto px-6 pt-6">
              <h2 className="mb-6 text-sm font-semibold text-black">
                {t('pages.create_edit_course.available_languages')}
              </h2>

              <div>
                {competenceSyncMessage && (
                  <p
                    className={`mb-4 text-sm ${
                      competenceSyncMessage.isError
                        ? 'text-error-05'
                        : 'text-success-03'
                    }`}
                  >
                    {competenceSyncMessage.text}
                  </p>
                )}
                {translations.map((translation) => (
                  <div key={translation.id}>
                    <div className="mb-6 flex items-center justify-between">
                      <div className="flex items-center">
                        <span>
                          {langData.find(
                            (lang) =>
                              lang.value === translation.lang && lang.flag
                          ) ? (
                            <img
                              src={`/images/flags/${translation.lang}.svg`}
                              alt={`${translation.lang} flag`}
                              className="
                                h-[32px] w-[32px]
                                rounded-[50%]
                                object-cover
                                "
                            />
                          ) : (
                            <PersonAvatar
                              id={translation.id}
                              name={translation.name as string}
                              imageSrc={
                                `/images/flags/${translation.lang}.svg` ||
                                undefined
                              }
                            />
                          )}
                        </span>
                        <span className="ml-3">
                          {t(`langs.${translation.lang}`)}
                        </span>
                      </div>

                      <div className="flex items-center gap-2 text-sm font-medium">
                        {props.publication.id === translation.id ? (
                          <Button variant="ghost" disabled>
                            {t('pages.create_edit_course.current_version')}
                          </Button>
                        ) : (
                          <>
                            {props.publication.id === languageMasterId &&
                              (isGeneralAdmin ||
                                userTranslations.includes(translation.id)) && (
                                <Button
                                  variant="outline"
                                  disabled={
                                    syncingTranslationId === translation.id
                                  }
                                  onClick={() =>
                                    handleCompetenceSync(translation)
                                  }
                                >
                                  {syncingTranslationId === translation.id
                                    ? t(
                                        'pages.create_edit_course.competence_syncing'
                                      )
                                    : t(
                                        'pages.create_edit_course.sync_competences'
                                      )}
                                </Button>
                              )}
                            <ButtonLink
                              className="item-center flex gap-2 border"
                              to={`${basePath}/${translation.id}`}
                              variant="outline"
                              onClick={() => props.setRefreshPage(true)}
                              /*
                          className={` ${
                            !userTranslations.includes(translation.id) &&
                            'pointer-events-none !bg-grey-02 !text-grey-04'
                          }`}
                          */
                            >
                              <p>
                                {t('pages.create_edit_course.go_to_button')}
                                <span className="mx-1">
                                  {t(`langs.${translation.lang}`)}
                                </span>
                                {t('pages.create_edit_course.go_to_button_2')}
                              </p>
                              <ArrowRight className="h-5 w-5 text-grey-04" />
                            </ButtonLink>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {lang.value && (
                  <div>
                    <div className="mb-6 flex items-center justify-between">
                      <div className="flex items-center">
                        <span>
                          {langData.find(
                            (data) => data.value === lang.value && data.flag
                          ) ? (
                            <img
                              src={`/images/flags/${lang.value}.svg`}
                              alt={`${lang.value} flag`}
                              className="
                                h-[32px] w-[32px]
                                rounded-[50%]
                                object-cover
                                "
                            />
                          ) : (
                            <PersonAvatar
                              id={lang.id}
                              name={lang.name as string}
                              imageSrc={
                                `/images/flags/${lang.value}.svg` || undefined
                              }
                            />
                          )}
                        </span>

                        <span className="ml-3">{t(`langs.${lang.value}`)}</span>
                      </div>

                      <div className="text-sm font-medium">
                        {props.publication.lang === lang.value ? (
                          <Button variant="ghost">
                            {t('pages.create_edit_course.current_version')}
                          </Button>
                        ) : (
                          <div className="item-center flex">
                            <Button
                              variant="outline"
                              className="item-center pointer-events-none flex gap-2 border"
                            >
                              {t('pages.create_edit_course.translating')}
                              <Spinner
                                ariaLabel="Loading spinner"
                                className="my-auto mr-2 h-5 w-5 border-grey-05"
                              />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-2 border-t border-b px-6 py-6">
              {asyncStarted ? (
                <div className="flex flex-col gap-2">
                  <h4 className="font-semibold">
                    {t(
                      'pages.create_edit_course.translation_started_async_title'
                    )}
                  </h4>
                  <p className="text-sm text-grey-06">
                    {t(
                      'pages.create_edit_course.translation_started_async_body'
                    )}
                  </p>
                </div>
              ) : lang.value ? (
                <div className="flex flex-col">
                  <h4 className="mb-2">
                    {t('pages.create_edit_course.automatic_translation')}
                  </h4>
                  <p className="text-sm text-grey-06">
                    {t('pages.create_edit_course.modal_text_lng_processing')}
                  </p>
                </div>
              ) : (
                <>
                  <div className="w-full">
                    <h4 className="mb-2">
                      {t('pages.create_edit_course.automatic_translation')}
                    </h4>
                    <p className="text-sm text-grey-06">
                      {t('pages.create_edit_course.modal_text_lng')}
                      <Link
                        to="https://teo.training/en/help/faqs/53"
                        className="ml-2 text-[#1173dd]"
                        target="_blank"
                      >
                        {t('pages.create_edit_course.modal_linked_text_lng')}
                        <OpenExternalLink className="ml-1 -mt-1 inline-block h-4 w-4 text-[#1173dd]" />
                      </Link>
                    </p>
                  </div>
                  <div className="mt-6 flex items-center gap-4">
                    <div className="flex w-[40%] items-center gap-3 rounded-md bg-grey-01 px-3 py-2">
                      <span>
                        {langData.find(
                          (lang) =>
                            lang.value === props.publication.lang && lang.flag
                        ) ? (
                          <img
                            src={`/images/flags/${props.publication.lang}.svg`}
                            alt={`${props.publication.lang} flag`}
                            className="
                                h-[20px] w-[20px]
                                rounded-[50%]
                                object-cover
                                "
                          />
                        ) : (
                          <PersonAvatar
                            id={props.publication.id}
                            name={props.publication.name as string}
                            imageSrc={
                              `/images/flags/${props.publication.lang}.svg` ||
                              undefined
                            }
                          />
                        )}
                      </span>
                      <span className="text-grey-08">
                        {t(`langs.${props.publication.lang}`)}
                      </span>
                    </div>
                    <div className="mx-auto">To</div>
                    <div className="w-[40%]">
                      <SelectDropdown
                        clearButtonLabel="clear"
                        onChange={async (lang: any) => {
                          // setIsOpenConfirmModal(true);
                          setSelectLang(lang);
                          if (['en-GB', 'en-US'].includes(lang.value))
                            setSelectedService('gpt');
                        }}
                        buttonText={`${t(
                          'pages.create_edit_course.select_language'
                        )}`}
                        options={translationLanguages}
                        searchable={true}
                        value={selectLang?.name ? selectLang : undefined}
                      />
                    </div>
                  </div>
                  {(isGeneralAdmin || orgChatGptTranslateEnabled) && (
                    <div className="mt-4">
                      <h2 className="mb-6 text-sm font-semibold text-black">
                        {t('pages.create_edit_course.translate_service')}
                      </h2>
                      <InputSelect
                        options={availableTranslateServices}
                        value={availableTranslateServices.find(
                          (y) => y.value === selectedService
                        )}
                        onChange={(data) => {
                          setSelectedService(data.value);
                          if (
                            data.value !== 'gpt' &&
                            ['en-GB', 'en-US'].includes(selectLang.value)
                          ) {
                            setSelectLang({ id: '', name: '', value: '' });
                          }
                        }}
                      >
                        {(option) => <span>{t(option.label)}</span>}
                      </InputSelect>
                    </div>
                  )}
                  <div className="mt-5 flex flex-row items-center gap-4 rounded-lg border border-grey-02 p-4">
                    <div data-testid="printLessonTitles">
                      <Checkbox
                        checked={translateSubtitles}
                        onChange={(isCheck) => setTranslateSubtitles(isCheck)}
                      />
                    </div>
                    <div>
                      <h4 className="pb-2 font-normal">
                        {t('pages.create_edit_course.translate_subtitles')}
                      </h4>
                      <p className="text-xs text-grey-05">
                        {t(
                          'pages.create_edit_course.translate_subtitles_explain'
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="mt-6">
                    <Button
                      variant="outline"
                      onClick={async () => {
                        await setLang(selectLang);
                        handleTranslate(selectLang);
                      }}
                    >
                      {t('pages.create_edit_course.start_translation')}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer
          cancelButtonLabel={t('pages.create_edit_course.cancel_button')}
          onCancel={() => {
            props.onClose();
          }}
        >
          {asyncStarted && (
            <div className="ml-auto">
              <Button onClick={() => props.onClose()}>{t('button.ok')}</Button>
            </div>
          )}
          <div>
            {/* <FilterDropdown
              clearButtonLabel="clear"
              onChange={async (lang: any) => {
                setIsOpenConfirmModal(true);
                setSelectLang(lang);
              }}
              buttonText={`${t(
                'pages.create_edit_course.translate_automatically_button'
              )}`}
              options={translationLanguages}
              searchable={true}
              value={selectLang?.name ? selectLang : undefined}
            /> */}
          </div>
        </Modal.Footer>
      </Modal>

      <ConfirmTranslationModal
        service={selectedService}
        isOpenConfirmModal={isOpenConfirmModal}
        setIsOpenConfirmModal={setIsOpenConfirmModal}
        handleTranslate={handleTranslate}
        setLang={setLang}
        selectLang={selectLang}
        setSelectLang={setSelectLang}
      />
    </>
  );
};
